import Config from "../appconfig.json";
import store from "../vuex/store";
import offlineConfig from "../config/offlineMode";
import {
  getUserManager,
  authService,
  redirectToIdPForNewTokenAfterWorkspaceSwitch,
  applyNewTokenFromWorkspaceSwitch,
} from "../js/authentication/authentication";

const IS_E2E_MODE = process.env.VUE_APP_E2E === "true";

function shouldBypassWorkspaceApis() {
  return offlineConfig.isOfflineMode && offlineConfig.bypassAuth && !IS_E2E_MODE;
}

/**
 * Service for IXP Identity Server multi-tenant workspace/tenant switching.
 * Uses platformEndpoints.authentication as base URL; requests send Bearer token.
 * Pass accessToken (from store.state.authentication.accessToken) to avoid 401.
 */
class WorkspaceService {
  constructor() {
    this.baseUrl = (
      process.env.VUE_APP_IDENTITY_SERVER_URL ||
      Config.platformEndpoints.authentication
    ).replace(/\/$/, "");
    /** From last successful POST /api/workspace/adopt 200 — sent as If-None-Match on the next adopt. */
    this._lastAdoptETag = null;
  }

  /** Call on logout so the next session does not send a stale ETag. */
  clearAdoptETag() {
    this._lastAdoptETag = null;
  }

  _resolveUserManager() {
    try {
      if (typeof getUserManager === "function") {
        const um = getUserManager();
        if (um) return um;
      }
    } catch (e) {
      // Fall back to authService.userManager below.
    }

    if (authService && authService.userManager) {
      return authService.userManager;
    }

    return null;
  }

  /**
   * Prefer oidc-client UserManager over Vuex: Vuex `accessToken` can lag behind after
   * silent renew / storeUser, causing 401s on Identity Server workspace APIs.
   * Explicit non-empty override (e.g. brand-new token from POST /switch) wins.
   */
  async _getAccessToken(accessToken) {
    if (accessToken && typeof accessToken === "string" && accessToken.trim()) {
      return accessToken.trim();
    }

    // Prefer in-memory monitor token first; OIDC storage can lag after cross-app workspace switches.
    try {
      const crossSync = await import("./workspaceCrossAppSync");
      if (crossSync && typeof crossSync.getMonitorCurrentUserAccessToken === "function") {
        const monitorToken = crossSync.getMonitorCurrentUserAccessToken();
        if (monitorToken && typeof monitorToken === "string" && monitorToken.trim()) {
          return monitorToken.trim();
        }
      }
    } catch (e) {
      // ignore dynamic import failures and continue to standard token sources
    }

    const userManager = this._resolveUserManager();
    if (userManager && typeof userManager.getUser === "function") {
      const user = await userManager.getUser();
      if (user && user.access_token) {
        return user.access_token;
      }
    }
    const fromStore =
      store.state.authentication && store.state.authentication.accessToken;
    return (fromStore && String(fromStore).trim()) || "";
  }

  /** Public: same token resolution as workspace fetch calls (for ping / tenant decode). */
  async getResolvedAccessToken(explicitOverride) {
    return this._getAccessToken(explicitOverride);
  }

  async _getHeaders(accessToken) {
    const token = await this._getAccessToken(accessToken);
    const headers = { "Content-Type": "application/json" };
    if (token) {
      headers.Authorization = "Bearer " + token;
    }
    return headers;
  }

  async getCurrentWorkspace(accessToken) {
    if (shouldBypassWorkspaceApis()) {
      return { selectedTenantId: null, accessibleTenantIds: [], primaryTenantId: null };
    }
    try {
      const url = `${this.baseUrl}/api/workspace/current?_=${Date.now()}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "omit",
        headers: await this._getHeaders(accessToken),
      });
      if (!response.ok) throw new Error("Failed to get current workspace");
      return await response.json();
    } catch (error) {
      console.error("Error getting current workspace:", error);
      throw error;
    }
  }

  /**
   * GET /api/workspace/ping?currentTenantId= — server reads **selected_tenant from the Bearer**
   * and compares to the DB.
   * When `needsRefresh: true`, the response may include **`access_token`** and **`expires_in`**
   * (corrected JWT) — client should apply immediately and skip extra signinSilent/adopt round-trips.
   * `currentTenantId` query param is legacy (not used for comparison).
   */
  async pingWorkspace(accessToken, currentTenantId) {
    if (shouldBypassWorkspaceApis()) {
      return null;
    }
    try {
      const url = new URL(`${this.baseUrl}/api/workspace/ping`);
      url.searchParams.set(
        "currentTenantId",
        currentTenantId != null ? String(currentTenantId) : ""
      );
      url.searchParams.set("_", String(Date.now()));
      const response = await fetch(url.toString(), {
        method: "GET",
        credentials: "include",
        headers: await this._getHeaders(accessToken),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn("Error pinging workspace:", error);
      return null;
    }
  }

  /**
   * POST /api/workspace/adopt — returns a fresh access token for the DB's current tenant (no DB write,
   * no cooldown). Used when signinSilent() still leaves a stale selected_tenant in the JWT.
   * Sends **If-None-Match** from the previous adopt **ETag** (304 = workspace unchanged; no new JWT).
   */
  async adoptWorkspace(accessToken) {
    if (shouldBypassWorkspaceApis()) {
      return null;
    }
    try {
      const headers = await this._getHeaders(accessToken);
      if (this._lastAdoptETag) {
        headers["If-None-Match"] = this._lastAdoptETag;
      }
      const response = await fetch(`${this.baseUrl}/api/workspace/adopt`, {
        method: "POST",
        credentials: "omit",
        headers,
      });
      if (response.status === 304) {
        return { notModified: true };
      }
      if (!response.ok) {
        console.warn("[Workspace] POST /adopt failed:", response.status);
        return null;
      }
      const etag = response.headers.get("ETag");
      if (etag) {
        this._lastAdoptETag = etag;
      }
      return await response.json();
    } catch (error) {
      console.warn("Error calling workspace adopt:", error);
      return null;
    }
  }

  async getAvailableWorkspaces(accessToken) {
    if (shouldBypassWorkspaceApis()) {
      return [];
    }
    try {
      const response = await fetch(`${this.baseUrl}/api/workspace/available`, {
        method: "GET",
        credentials: "omit",
        headers: await this._getHeaders(accessToken),
      });
      if (!response.ok) throw new Error("Failed to get available workspaces");
      return await response.json();
    } catch (error) {
      console.error("Error getting available workspaces:", error);
      throw error;
    }
  }

  /**
   * POST /api/workspace/switch — user-initiated workspace change.
   * On **200**, the response body must include **`access_token`** (and ideally **`expires_in`**); this method
   * applies it via **`applyNewTokenFromWorkspaceSwitch`** and does **not** call **`/adopt`** — adopt is only for
   * catching up when another app changed workspace (see **`workspaceCrossAppSync`** + **`/ping`**).
   */
  async switchWorkspace(tenantId, accessToken) {
    if (shouldBypassWorkspaceApis()) {
      throw new Error("Workspace switch not available in offline mode");
    }
    try {
      const response = await fetch(`${this.baseUrl}/api/workspace/switch`, {
        method: "POST",
        credentials: "include",
        headers: await this._getHeaders(accessToken),
        body: JSON.stringify({ tenantId }),
      });

      // Server cooldown: another tab/app just switched; DB already has the target tenant.
      // Do not throw — treat as success and sync via signinSilent + UI (see WorkspaceSwitcher).
      if (response.status === 409) {
        const body = await response.json().catch(() => ({}));
        const currentTenantId =
          body.currentTenantId != null
            ? body.currentTenantId
            : body.current_tenant_id != null
              ? body.current_tenant_id
              : null;
        console.warn(
          "[Workspace] Switch cooldown (409) — server already on",
          currentTenantId,
          body.message || ""
        );
        return {
          cooldown: true,
          currentTenantId,
          tenantId: currentTenantId,
          tokenApplied: false,
          error: body.error,
          message: body.message,
          retryAfterSeconds: body.retryAfterSeconds,
        };
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.message || "Failed to switch workspace");
      }

      let result;
      try {
        result = await response.json();
      } catch (e) {
        console.error("[Workspace] Switch response was not JSON:", e);
        throw new Error("Invalid response from workspace switch API");
      }

      const newAccessToken =
        (result.access_token != null ? result.access_token : result.accessToken) ||
        (result.data &&
          (result.data.access_token != null
            ? result.data.access_token
            : result.data.accessToken)) ||
        result.token;
      const newExpiresIn =
        (result.expires_in != null ? result.expires_in : result.expiresIn) ||
        (result.data &&
          (result.data.expires_in != null
            ? result.data.expires_in
            : result.data.expiresIn));

      if (newAccessToken) {
        await applyNewTokenFromWorkspaceSwitch(newAccessToken, newExpiresIn);
        result.tokenApplied = true;
        result.newAccessToken = newAccessToken;
      } else {
        if (window.sessionStorage) {
          sessionStorage.setItem("workspace_switch_pending", tenantId);
        }
        console.warn(
          "[Workspace] Switch API returned only:",
          Object.keys(result),
          "- missing access_token/accessToken. Add access_token and expires_in to the response so the app can switch without redirect."
        );
        redirectToIdPForNewTokenAfterWorkspaceSwitch();
      }

      import("./workspaceCrossAppSync").then((m) => {
        m.notifyWorkspaceSwitchAcrossApps(result);
      });

      return result;
    } catch (error) {
      console.error("Error switching workspace:", error);
      throw error;
    }
  }

  setupCrossTabSync(onWorkspaceChanged) {
    if (!window.addEventListener || !window.localStorage) return;

    window.addEventListener("storage", async (e) => {
      if (e.key !== "workspace_changed") return;
      try {
        const data = JSON.parse(e.newValue || "{}");
        const userManager = this._resolveUserManager();
        if (userManager && typeof userManager.signinSilent === "function") {
          try {
            await userManager.signinSilent();
          } catch (err) {
            console.warn("Cross-tab silent refresh failed:", err);
          }
        }
        if (typeof onWorkspaceChanged === "function") {
          onWorkspaceChanged(data.tenantId);
        }
      } catch (error) {
        console.error("Error handling workspace change from other tab:", error);
      }
    });
  }

  async getSelectedTenantFromClaims() {
    if (shouldBypassWorkspaceApis()) return null;
    const userManager = this._resolveUserManager();
    if (!userManager || typeof userManager.getUser !== "function") return null;
    const user = await userManager.getUser();
    if (!user || !user.profile) return null;
    return (
      user.profile.selected_tenant ||
      user.profile.primary_tenant ||
      null
    );
  }
}

export const workspaceService = new WorkspaceService();
